/**
 * media-tab.tsx — Cài đặt → Media. Quản lý kho tệp trên máy chủ.
 *
 * Trước màn này, tệp trong hệ thống chỉ được TẠO ra chứ không có đường xoá:
 * tải lên, tải về từ nguồn, giải mã từ base64. Xoá một tài liệu trong thư viện
 * chỉ mất bản ghi, tệp vẫn nằm lại trên đĩa mãi mãi.
 *
 * Việc quan trọng nhất ở đây là phân biệt ĐANG DÙNG với LẠC, và nói rõ tệp
 * đang dùng thì dùng ở đâu — xoá nhầm một ảnh sản phẩm thì khách nhận được
 * thẻ ảnh hỏng, mà không ai biết vì sao.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import {
  FileText, Film, HardDrive, Image as ImageIcon, Loader2, Paperclip, Search, Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import {
  coChu, useDonTepLac, useMedia, useXoaTepMedia,
  type BoLocMedia, type LoaiTep, type TepMedia,
} from '@/hooks/use-media'

const LOAI: { id: LoaiTep; nhan: string; icon: typeof ImageIcon }[] = [
  { id: 'anh', nhan: 'Ảnh', icon: ImageIcon },
  { id: 'video', nhan: 'Video', icon: Film },
  { id: 'pdf', nhan: 'PDF', icon: FileText },
  { id: 'tai-lieu', nhan: 'Tài liệu', icon: FileText },
  { id: 'khac', nhan: 'Khác', icon: Paperclip },
]

function iconLoai(l: LoaiTep) {
  return LOAI.find((x) => x.id === l)?.icon ?? Paperclip
}

export function MediaTab() {
  const [loc, setLoc] = useState<BoLocMedia>({})
  const [tim, setTim] = useState('')
  const [chon, setChon] = useState<Set<string>>(new Set())
  const [hoiXoa, setHoiXoa] = useState(false)
  const [hoiDon, setHoiDon] = useState(false)

  const { data, isLoading, isError } = useMedia({ ...loc, tim: tim.trim() || undefined })
  const xoa = useXoaTepMedia()
  const don = useDonTepLac()

  const doiChon = (khoa: string) => {
    setChon((c) => {
      const n = new Set(c)
      if (n.has(khoa)) n.delete(khoa)
      else n.add(khoa)
      return n
    })
  }

  const tepDangHien = data?.tep ?? []
  const chonHopLe = tepDangHien.filter((t) => chon.has(t.khoa))
  const soDangDungBiChon = chonHopLe.filter((t) => t.dangDung).length

  const doiLoc = (moi: Partial<BoLocMedia>) => {
    setLoc((c) => ({ ...c, ...moi }))
    setChon(new Set())
  }

  if (isError) return <ErrorState message="Không đọc được kho tệp trên máy chủ" />

  return (
    <div className="space-y-5">
      {/* Tổng quan + nút dọn */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5">
          <HardDrive className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">
            <strong className="tabular-nums">{coChu(data?.tong.dungLuong ?? 0)}</strong>{' '}
            <span className="text-muted-foreground">
              trong {(data?.tong.soTep ?? 0).toLocaleString('vi-VN')} tệp
            </span>
          </span>
        </div>
        {(data?.tong.soTepLac ?? 0) > 0 && (
          <>
            <div className="flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-2.5 text-sm text-warning">
              <Trash2 className="h-4 w-4" />
              <span>
                <strong className="tabular-nums">{coChu(data?.tong.dungLuongLac ?? 0)}</strong> tệp lạc —{' '}
                {(data?.tong.soTepLac ?? 0).toLocaleString('vi-VN')} tệp không bản ghi nào dùng tới
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setHoiDon(true)}>
              <Trash2 className="h-4 w-4" />
              Dọn tệp lạc
            </Button>
          </>
        )}
      </div>

      {/* Từng kho */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(data?.thongKe ?? []).map((k) => {
          const dangChon = loc.kho === k.id
          return (
            <Card
              key={k.id}
              onClick={() => doiLoc({ kho: dangChon ? undefined : k.id })}
              className={cn(
                'cursor-pointer p-4 transition-colors',
                dangChon ? 'border-primary bg-primary/5' : 'hover:border-primary/40',
              )}
            >
              <p className="text-sm font-semibold">{k.ten}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{k.moTa}</p>
              <p className="mt-2 text-lg font-bold tabular-nums">{coChu(k.dungLuong)}</p>
              <p className="text-xs text-muted-foreground">
                {k.soTep.toLocaleString('vi-VN')} tệp
                {k.soTepLac > 0 && (
                  <span className="text-warning"> · {k.soTepLac.toLocaleString('vi-VN')} lạc</span>
                )}
              </p>
            </Card>
          )
        })}
      </div>

      {/* Bộ lọc */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={tim}
            onChange={(e) => setTim(e.target.value)}
            placeholder="Tìm theo tên tệp hoặc nơi đang dùng…"
            className="h-8 w-64 pl-8 text-xs"
          />
        </div>
        {LOAI.map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => doiLoc({ loai: loc.loai === l.id ? undefined : l.id })}
            className={cn(
              'rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors',
              loc.loai === l.id ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent',
            )}
          >
            {l.nhan}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-border" />
        {([['lac', 'Chỉ tệp lạc'], ['dung', 'Chỉ tệp đang dùng']] as const).map(([id, nhan]) => (
          <button
            key={id}
            type="button"
            onClick={() => doiLoc({ trangThai: loc.trangThai === id ? undefined : id })}
            className={cn(
              'rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors',
              loc.trangThai === id ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent',
            )}
          >
            {nhan}
          </button>
        ))}
        {chon.size > 0 && (
          <Button
            variant="destructive"
            size="sm"
            className="ml-auto"
            onClick={() => setHoiXoa(true)}
          >
            <Trash2 className="h-4 w-4" />
            Xoá {chon.size} tệp đã chọn
          </Button>
        )}
      </div>

      {/* Danh sách */}
      {isLoading ? (
        <Loading />
      ) : tepDangHien.length === 0 ? (
        <p className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
          Không có tệp nào khớp bộ lọc.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {(data?.soDong ?? 0).toLocaleString('vi-VN')} tệp khớp bộ lọc
            {data?.catBot && ' · đang hiện 500 tệp nặng nhất'}
          </p>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="bg-muted/70 text-left">
                <tr>
                  <th className="w-10 px-2 py-2">
                    <input
                      type="checkbox"
                      aria-label="Chọn tất cả"
                      checked={chon.size > 0 && chonHopLe.length === tepDangHien.length}
                      onChange={(e) =>
                        setChon(e.target.checked ? new Set(tepDangHien.map((t) => t.khoa)) : new Set())
                      }
                    />
                  </th>
                  <th className="px-3 py-2">Tệp</th>
                  <th className="px-3 py-2">Kho</th>
                  <th className="px-3 py-2">Dung lượng</th>
                  <th className="px-3 py-2">Trạng thái</th>
                  <th className="px-3 py-2">Sửa lúc</th>
                </tr>
              </thead>
              <tbody>
                {tepDangHien.map((t) => (
                  <HangTep key={t.khoa} tep={t} chon={chon.has(t.khoa)} onChon={() => doiChon(t.khoa)} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Hỏi trước khi xoá thủ công */}
      <Dialog open={hoiXoa} onOpenChange={setHoiXoa}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Xoá {chon.size} tệp khỏi máy chủ?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Tệp bị xoá vĩnh viễn khỏi đĩa. Không có thùng rác.</p>
                {soDangDungBiChon > 0 && (
                  <p className="rounded-md bg-destructive/10 px-3 py-2 font-medium text-destructive">
                    Trong đó có {soDangDungBiChon} tệp ĐANG ĐƯỢC DÙNG. Xoá xong, chỗ đang dùng nó
                    sẽ hiện ảnh hỏng — kể cả ảnh đã gửi cho khách.
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoiXoa(false)}>Huỷ</Button>
            <Button
              variant="destructive"
              disabled={xoa.isPending}
              onClick={() =>
                xoa.mutate([...chon], {
                  onSuccess: (r) => {
                    toast.success(`Đã xoá ${r.daXoa} tệp`)
                    if (r.boQua.length) toast.warning(`${r.boQua.length} tệp không xoá được`)
                    setChon(new Set())
                    setHoiXoa(false)
                  },
                  onError: (e) => toast.error(apiError(e)),
                })
              }
            >
              {xoa.isPending && <Loader2 className="animate-spin" />}
              Xoá vĩnh viễn
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hỏi trước khi dọn tệp lạc */}
      <Dialog open={hoiDon} onOpenChange={setHoiDon}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dọn tệp lạc?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Xoá {(data?.tong.soTepLac ?? 0).toLocaleString('vi-VN')} tệp mà không bản ghi nào
                  trong cơ sở dữ liệu trỏ tới, lấy lại{' '}
                  <strong>{coChu(data?.tong.dungLuongLac ?? 0)}</strong>.
                </p>
                <p className="text-muted-foreground">
                  Tệp đang dùng không bị đụng tới. Việc này không hoàn tác được.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoiDon(false)}>Huỷ</Button>
            <Button
              variant="destructive"
              disabled={don.isPending}
              onClick={() =>
                don.mutate(loc.kho, {
                  onSuccess: (r) => {
                    toast.success(`Đã dọn ${r.daXoa} tệp, lấy lại ${coChu(r.dungLuong)}`)
                    setHoiDon(false)
                  },
                  onError: (e) => toast.error(apiError(e)),
                })
              }
            >
              {don.isPending && <Loader2 className="animate-spin" />}
              Dọn ngay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function HangTep({ tep, chon, onChon }: { tep: TepMedia; chon: boolean; onChon: () => void }) {
  const Icon = iconLoai(tep.loai)
  return (
    <tr className={cn('border-t align-middle hover:bg-accent/40', chon && 'bg-primary/5')}>
      <td className="px-2 py-1.5">
        <input type="checkbox" checked={chon} onChange={onChon} aria-label={`Chọn ${tep.ten}`} />
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-2">
          {tep.loai === 'anh' ? (
            <img
              src={tep.url}
              alt=""
              loading="lazy"
              className="h-9 w-9 shrink-0 rounded border object-cover"
            />
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border bg-muted">
              <Icon className="h-4 w-4 text-muted-foreground" />
            </span>
          )}
          <a
            href={tep.url}
            target="_blank"
            rel="noreferrer"
            className="max-w-[22rem] truncate font-medium hover:underline"
            title={tep.ten}
          >
            {tep.ten}
          </a>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">{tep.kho}</td>
      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{coChu(tep.kichThuoc)}</td>
      <td className="px-3 py-1.5">
        {tep.dangDung ? (
          <span className="text-muted-foreground" title={tep.dungO}>
            Đang dùng{tep.dungO ? ` · ${tep.dungO}` : ''}
          </span>
        ) : (
          <span className="font-semibold text-warning">Lạc</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">
        {dayjs(tep.suaLuc).format('DD/MM/YYYY HH:mm')}
      </td>
    </tr>
  )
}
